import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Crown, Sparkles, Upload, RotateCcw, Images } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { LGAS, ETHNIC_GROUPS } from "@/lib/data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SocialShare from "@/components/SocialShare";

const HERO = "https://images.unsplash.com/photo-1516617442634-75371039cb3a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function CulturalRunway() {
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", lga: "", ethnic_group: "", gender: "", notes: "", attire_photo: "" });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const [gallery, setGallery] = useState([]);
  const [galleryFilter, setGalleryFilter] = useState("all");
  useEffect(() => {
    const params = galleryFilter !== "all" ? { ethnic_group: galleryFilter } : {};
    api.get("/cultural-runway/gallery", { params }).then((r) => setGallery(r.data)).catch(() => {});
  }, [galleryFilter]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f, f.name);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("attire_photo", data.path);
      toast.success("Attire photo uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.lga || !form.ethnic_group || !form.gender)
      return toast.error("Please complete all required fields");
    setSubmitting(true);
    try {
      await api.post("/cultural-runway", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <main className="pt-28 pb-20 min-h-screen">
        <div className="max-w-xl mx-auto px-5 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-9 h-9 text-emerald-400" />
          </motion.div>
          <div className="overline-label mb-3">Nasarawa @30 · Cultural Runway</div>
          <h1 className="font-heading text-3xl font-extrabold">You're on the list!</h1>
          <p className="text-zinc-400 mt-3">Thank you for volunteering to represent <span className="text-[#F5A623] font-semibold">{form.ethnic_group}</span> culture on the runway. Our team will reach out with rehearsal and event details.</p>
          <Button onClick={() => { setDone(false); setForm({ full_name: "", phone: "", email: "", lga: "", ethnic_group: "", gender: "", notes: "", attire_photo: "" }); }} variant="outline" className="mt-8 border-white/15 bg-transparent rounded-full">
            <RotateCcw className="w-4 h-4 mr-2" /> Submit Another
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-16 min-h-screen">
      <section className="relative grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C0E]/70 via-[#0B0C0E]/85 to-[#0B0C0E]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 pt-16 pb-14 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E6197E]/15 border border-[#E6197E]/40 mb-6">
            <Crown className="w-4 h-4 text-[#F5A623]" />
            <span className="text-xs font-bold uppercase tracking-widest brand-text-gradient">Celebrating Nasarawa @30</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Nasarawa State, <span className="brand-text-gradient">Reimagined</span>
          </h1>
          <p className="mt-5 text-lg text-zinc-300 max-w-xl mx-auto leading-relaxed">
            We're staging a <strong className="text-white">Cultural Runway</strong> celebrating the peoples of Nasarawa. If you own and can wear the traditional attire of your ethnic group, volunteer to showcase your heritage on the big day.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-1.5 max-w-2xl mx-auto">
            {ETHNIC_GROUPS.map((g) => (
              <span key={g} className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-zinc-300">{g}</span>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <SocialShare url={`${window.location.origin}/cultural-runway`} text="Celebrating Nasarawa @30 — join the Cultural Runway and showcase your heritage attire! Volunteer here:" label="Share this call" testid="cultural-share" />
          </div>
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-5 sm:px-8 pb-24 -mt-2">
        <div className="rounded-3xl border border-white/10 bg-[#131519] p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-6"><Sparkles className="w-5 h-5 text-[#E6197E]" /><h2 className="font-heading text-xl font-bold">Volunteer for the Cultural Runway</h2></div>
          <form onSubmit={submit} data-testid="cultural-form" className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Full Name *</Label><Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="cultural-name-input" /></div>
              <div><Label>Phone *</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="cultural-phone-input" /></div>
            </div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 bg-[#0B0C0E] border-white/10" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>LGA *</Label>
                <Select value={form.lga} onValueChange={(v) => set("lga", v)}>
                  <SelectTrigger className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="cultural-lga-select"><SelectValue placeholder="Select LGA" /></SelectTrigger>
                  <SelectContent>{LGAS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Gender *</Label>
                <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
                  <SelectTrigger className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="cultural-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Ethnic Group / Attire You Can Wear *</Label>
              <Select value={form.ethnic_group} onValueChange={(v) => set("ethnic_group", v)}>
                <SelectTrigger className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="cultural-ethnic-select"><SelectValue placeholder="Select your heritage attire" /></SelectTrigger>
                <SelectContent className="max-h-64">{ETHNIC_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Photo of Your Attire (optional)</Label>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="cultural-photo-input" />
              <Button type="button" variant="outline" className="mt-2 border-white/15 bg-transparent" disabled={uploading} onClick={() => fileRef.current?.click()}>
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} {form.attire_photo ? "Photo added" : "Upload Photo"}
              </Button>
            </div>
            <div><Label>Notes (optional)</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything we should know — sizes, availability, group performances..." className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="cultural-notes-input" /></div>
            <Button type="submit" disabled={submitting} className="w-full h-12 bg-[#E6197E] hover:bg-[#C4136B] text-base font-semibold rounded-full" data-testid="cultural-submit-button">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Volunteer for the Runway"}
            </Button>
          </form>
        </div>
      </section>

      {gallery.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24" data-testid="attire-gallery">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <div className="overline-label mb-2 flex items-center gap-2"><Images className="w-4 h-4" /> Heritage Showcase</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold">Attire Gallery</h2>
              <p className="text-zinc-400 text-sm mt-2 max-w-lg">Meet the volunteers already set to grace the runway in the traditional attires of Nasarawa.</p>
            </div>
            <Select value={galleryFilter} onValueChange={setGalleryFilter}>
              <SelectTrigger className="bg-[#131519] border-white/10 max-w-[240px]" data-testid="gallery-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64"><SelectItem value="all">All Ethnic Groups</SelectItem>{ETHNIC_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="columns-2 sm:columns-3 lg:columns-4 gap-4 [&>*]:mb-4">
            {gallery.map((g) => (
              <motion.div key={g.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="break-inside-avoid rounded-2xl overflow-hidden border border-white/10 bg-[#131519] group" data-testid="gallery-item">
                <div className="overflow-hidden">
                  <img src={fileUrl(g.attire_photo)} alt={`${g.ethnic_group} attire`} className="w-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-3.5">
                  <div className="text-xs font-bold text-[#F5A623]">{g.ethnic_group}</div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">{g.full_name?.split(" ")[0]} · {g.lga} LGA</div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
